import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Tooltip } from '@mui/material';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { apiService } from '../../config/api';

export default function StudyBucketButton({ studyId }: { studyId: string }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fallback, setFallback] = useState('');
  const current = useRef(0);
  useEffect(() => {
    const version = ++current.current;
    setLoading(false); setError(''); setFallback('');
    return () => { current.current = version + 1; };
  }, [studyId]);
  const open = async () => {
    const request = current.current;
    // Reserve the tab during the click so asynchronous auth does not trigger a
    // popup blocker. If blocked, expose an ordinary link after authorization.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setLoading(true); setError(''); setFallback('');
    try {
      const result = await apiService.get(`/api/studies/${encodeURIComponent(studyId)}/bucket-signed-url`);
      if (request !== current.current) { tab?.close(); return; }
      const url = new URL(result.signedUrl);
      if (!result.success || url.origin !== 'https://console.cloud.google.com' ||
        !url.pathname.startsWith('/storage/browser/') || url.username || url.password) throw new Error('Invalid bucket link');
      if (tab && !tab.closed) tab.location.replace(url.href);
      else setFallback(url.href);
    } catch {
      tab?.close();
      if (request === current.current) setError('Could not open this study’s bucket. Please retry.');
    } finally { if (request === current.current) setLoading(false); }
  };
  return <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
    <Tooltip title="Opens this study’s export bucket. Sign in with your authorized Google account.">
      <span><Button variant="outlined" onClick={open} disabled={loading || !studyId}
        startIcon={loading ? <CircularProgress size={18} /> : <CloudOutlined />} endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
        sx={{ minHeight: 44, borderRadius: '12px', width: { xs: '100%', sm: 'auto' } }}>
        {loading ? 'Opening bucket…' : 'Go to Google Cloud bucket'}
      </Button></span>
    </Tooltip>
    {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
    {fallback && <Alert severity="info" sx={{ mt: 1.5 }}>Your browser blocked the new tab. <Link href={fallback} target="_blank" rel="noopener noreferrer">Open Google Cloud bucket</Link></Alert>}
  </Box>;
}
